import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react';
import { api } from '../../services/api';
import { toast } from '../../store/toastStore';
import { apiErrorMessage } from '../sales/shared';
import { formatDateTime } from '../../utils/format';
import {
  coverageBannerMode, newEngagementPath, pendingRequest, showDecisionButtons,
  type CoverageApprovalScope, type TicketCoverageInfo,
} from '../../utils/coverageModel';
import CoverageRequestDialog from './CoverageRequestDialog';

/**
 * FIX-D300-S2 (D-300 sec.2-4) — the ticket page (OTE staff) of a service request whose customer has no coverage.
 *  - switch ON ('blocked'): "ללקוח הזה אין תקופת אחריות, בנק שעות או חוזה שירות" - work / reply / task are refused
 *    by the server; the only action is "שליחת הודעה למנהל" (or "נשלחה הודעה למנהל ב-…, ממתין").
 *  - switch OFF ('notice', ⭐): nothing is blocked; the same line, quieter, and "שליחת הודעה למנהל" as a request for
 *    permission (the manager may approve "בלי חיוב").
 *  - the customer's account manager / an OTE manager, with a pending request: the request + 3 buttons
 *    [הוספת בנק שעות] [הוספת חוזה שירות] [אישור לעבוד בלי חיוב] (this request only / every request of the customer).
 *  - an approval in force: one line "המנהל אישר לעבוד בלי חיוב".
 */
export default function TicketCoverageBanner({ ticketId, coverage }: { ticketId: string; coverage: TicketCoverageInfo | null | undefined }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [choosingScope, setChoosingScope] = useState(false);
  const [approving, setApproving] = useState(false);
  const mode = coverageBannerMode(coverage);

  if (coverage?.applies && coverage.approval) {
    return (
      <div className="flex items-center gap-3 rounded-lg bg-surface-50 px-4 py-3 text-sm text-text" role="status" data-testid="coverage-approved">
        <CheckCircle2 size={18} className="shrink-0 text-success-600" aria-hidden="true" />
        <p className="min-w-0 flex-1">{coverage.approval === 'ticket' ? t('coverage.approvedTicket') : t('coverage.approvedCustomer')}</p>
      </div>
    );
  }
  if (mode === 'none' || !coverage) return null;

  const pending = pendingRequest(coverage);
  const decide = showDecisionButtons(coverage);
  const approve = async (scope: CoverageApprovalScope) => {
    if (!pending) return;
    setApproving(true);
    try {
      await api.approveCoverageRequest(ticketId, pending.id, scope);
      toast('success', t('coverage.approvedToast'));
      setChoosingScope(false);
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
    } catch (err) {
      toast('error', apiErrorMessage(err, t('coverage.approveFailed')));
    } finally {
      setApproving(false);
    }
  };

  const blocked = mode === 'blocked';
  const tone = blocked ? 'bg-danger-50 text-danger-800' : 'bg-warning-50 text-warning-800';
  return (
    <div className={`space-y-3 rounded-lg px-4 py-3 ${tone}`} role="status" data-testid={blocked ? 'coverage-blocked' : 'coverage-notice'}>
      <div className="flex flex-wrap items-center gap-3">
        {blocked
          ? <ShieldAlert size={18} className="shrink-0" aria-hidden="true" />
          : <AlertTriangle size={18} className="shrink-0" aria-hidden="true" />}
        <p className="min-w-0 flex-1 text-sm font-medium">
          {t('coverage.blockedMessage')}
          {!blocked && <span className="block font-normal">{t('coverage.noticeHint')}</span>}
        </p>
        {pending ? (
          <span className="text-sm" data-testid="coverage-pending">{t('coverage.pendingSince', { at: formatDateTime(pending.created_at) })}</span>
        ) : (
          <button type="button" className={blocked ? 'btn btn-primary' : 'btn btn-secondary'} onClick={() => setDialogOpen(true)} data-testid="coverage-open-dialog">
            {t('coverage.sendToManager')}
          </button>
        )}
      </div>
      {decide && pending && coverage.organization_id && (
        <div className="space-y-2 border-t border-border pt-3 text-sm" data-testid="coverage-decision">
          <p dir="auto">
            {t('coverage.managerMessage', { customer: coverage.organization_name ?? '', subject: coverage.ticket_title ?? '' })}
            {pending.requested_by_name ? ` · ${t('coverage.sentBy', { name: pending.requested_by_name })}` : ''}
          </p>
          {pending.message_extra && <p className="whitespace-pre-wrap" dir="auto">{pending.message_extra}</p>}
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(newEngagementPath(coverage.organization_id!, 'hour_bank'))}>
              {t('coverage.addHourBank')}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(newEngagementPath(coverage.organization_id!, 'service_contract'))}>
              {t('coverage.addContract')}
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setChoosingScope((v) => !v)} aria-expanded={choosingScope} data-testid="coverage-approve">
              {t('coverage.approveWithoutBilling')}
            </button>
          </div>
          {choosingScope && (
            <div className="flex flex-wrap gap-2" role="group" aria-label={t('coverage.approveWithoutBilling')}>
              <button type="button" className="btn btn-secondary btn-sm" disabled={approving} onClick={() => void approve('ticket')} data-testid="coverage-approve-ticket">
                {t('coverage.scopeTicket')}
              </button>
              <button type="button" className="btn btn-secondary btn-sm" disabled={approving} onClick={() => void approve('customer')} data-testid="coverage-approve-customer">
                {t('coverage.scopeCustomer')}
              </button>
            </div>
          )}
        </div>
      )}
      <CoverageRequestDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        ticketId={ticketId}
        organizationName={coverage.organization_name}
        ticketTitle={coverage.ticket_title}
        blocked={blocked}
      />
    </div>
  );
}
